/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-functions-list.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LibraryGrid from '../../library-grid';
import { useBoolean, useGridCells, useTranslation } from 'hooks';
import { StudyFields, NamedItemFields } from 'queries/generated/fragments.generated';
import { ASSIGN_FUNCTIONS, GET_STUDY } from 'queries/studies';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import 'views/components/admin/studies/admin-study.scss';

const AssignFunctionsListDialog = ({
    study,
    open,
    onClose,
    availableFunctions,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    availableFunctions: NamedItemFields[];
}) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState([]);
    const [assignFunctions] = useMutation(ASSIGN_FUNCTIONS);
    const saving = useBoolean(false);
    const { MaxLengthCell } = useGridCells();

    const close = (action?: any) => {
        setSelectedItems([]);
        onClose(action);
    };

    const gridColumns = [
        {
            title: t('ra:function'),
            id: 'item-name',
            field: 'name',
            cells: { data: MaxLengthCell(50) },
        },
    ];

    const errorRequest = () => {
        setSelectedItems([]);
        NotificationService.showNotification(t('errorMessage'));
        saving.setFalse();
    };

    const successRequest = (closeModal: boolean) => {
        saving.setFalse();
        NotificationService.showNotification(
            t('admin:functionsuccessfullyAssigned', { id: study.recordId, amount: selectedItems.length }),
        );
        setSelectedItems([]);
        if (closeModal) onClose();
    };

    const associateFunctions = (closeModal?: boolean) => {
        const studyFunctionIds = study.studyFunctionIds?.concat(selectedItems.map((selected) => selected.id));

        saving.setTrue();

        assignFunctions({
            variables: { studyId: study.id, reason: '', studyFunctionIds },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest(closeModal);
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="assign-function-modal-title">
                {t('admin:assignFunctions')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="assign-function-dialog-content assign-dialog-content mb-20">
                <LibraryGrid
                    items={availableFunctions}
                    columns={gridColumns}
                    itemsType={t('admin:functionRecords')}
                    selection={{
                        selectedItems,
                        setSelectedItems,
                        showItemsCount: true,
                    }}
                />
            </DialogContent>
            <DialogActions className="actions-assign-function flex-wrap pt-0">
                <Button
                    id="assign-function-new"
                    text="new"
                    secondary
                    disabled={saving.value}
                    onClick={() => {
                        close('new-function');
                    }}
                />
                <Button
                    text="assign"
                    disabled={!selectedItems.length || saving.value}
                    id="assign-function-save"
                    onClick={() => {
                        associateFunctions(false);
                    }}
                />
                <Button
                    text="ra:assignAndClose"
                    disabled={!selectedItems.length || saving.value}
                    id="assign-function-save-close"
                    onClick={() => {
                        associateFunctions(true);
                    }}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignFunctionsListDialog;
